// frontend/src/components/Mobile/MobilePrepDetail.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { fastReactAPI } from '../../../api/client';
import {
  FiPlay,
  FiCheckCircle,
  FiCheck,
  FiRotateCcw,
  FiPrinter,
  FiCamera,
  FiX,
  FiAlertCircle,
  FiPackage,
} from 'react-icons/fi';
import jsQR from 'jsqr';
import MobileLayout from '../MobileLayout';
import '../Mobile.css';

// 1 decimal, no unit
const fmtQty = (v) =>
  v != null
    ? parseFloat(v).toLocaleString('pt-PT', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })
    : '—';

const advanceBtnClass = (s) =>
  s === 'SETUP'
    ? 'mb-advance-btn mb-advance-btn--setup'
    : s === 'RESTOCK'
      ? 'mb-advance-btn mb-advance-btn--setup'
      : s === 'IN_PROGRESS'
        ? 'mb-advance-btn mb-advance-btn--inprog'
        : s === 'READY'
          ? 'mb-advance-btn mb-advance-btn--disabled'
          : 'mb-advance-btn mb-advance-btn--disabled';

const advanceBtnIcon = (s) =>
  s === 'SETUP'
    ? <FiPlay size={16} />
    : s === 'RESTOCK'
      ? <FiPlay size={16} />
      : s === 'IN_PROGRESS'
        ? <FiCheckCircle size={16} />
        : <FiCheck size={16} />;

const itemBadge = (s) =>
  s === 'SETUP'
    ? <span className="mb-badge mb-badge--setup">SETUP</span>
    : s === 'IN_PROGRESS'
      ? <span className="mb-badge mb-badge--inprog">Em Prep.</span>
      : s === 'READY'
        ? <span className="mb-badge mb-badge--completed">Pronto p/ Recolha</span>
        : s === 'COMPLETED'
          ? <span className="mb-badge mb-badge--completed">Recolhido</span>
          : s === 'RESTOCK'
            ? <span className="mb-badge mb-badge--restock">Reabastecer</span>
            : null;

const MobilePrepDetail = () => {
  const { poSetupId } = useParams();

  const [setup, setSetup] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [advancing, setAdvancing] = useState(null);

  // Carrinho prompt state
  const [showCarrinho, setShowCarrinho] = useState(false);
  const [printState, setPrintState] = useState(null); // null|'loading'|'success'|'error'
  const [printMsg, setPrintMsg] = useState('');
  const [carrinhoInput, setCarrinhoInput] = useState('');
  const [pendingItem, setPendingItem] = useState(null);

  // Restock prompt state
  const [showRestock, setShowRestock] = useState(false);
  const [restockInput, setRestockInput] = useState('');
  const [pendingRestockItem, setPendingRestockItem] = useState(null);
  const [restocking, setRestocking] = useState(false);
  const restockInputRef = useRef(null);

  // Camera scan state
  const [scanningCarrinho, setScanningCarrinho] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [scanError, setScanError] = useState('');

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const carrinhoInputRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      if (!poSetupId || poSetupId === 'undefined') {
        console.error('[MobilePrepDetail] poSetupId is missing:', poSetupId);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const [setupRes, itemsRes] = await Promise.all([
          fastReactAPI.getPlanoTearesItem(poSetupId),
          fastReactAPI.getPreparationItems(poSetupId),
        ]);

        if (setupRes.data.success) setSetup(setupRes.data.data);
        if (itemsRes.data.success) setItems(itemsRes.data.items);
        else console.warn('[MobilePrepDetail] items NOT success:', itemsRes.data);
      } catch (e) {
        console.error('[MobilePrepDetail] Promise.all failed:', e.response?.data || e.message);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [poSetupId]);

  const stopScan = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());

    rafRef.current = null;
    streamRef.current = null;
    setScanningCarrinho(false);
    setCameraReady(false);
  }, []);

  useEffect(() => () => stopScan(), [stopScan]);

  const scanLoop = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas || !streamRef.current) return;

    if (
      video.readyState === video.HAVE_ENOUGH_DATA &&
      video.videoWidth > 0 &&
      video.videoHeight > 0
    ) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) {
        rafRef.current = requestAnimationFrame(scanLoop);
        return;
      }

      ctx.drawImage(video, 0, 0);

      // Skip black frames
      const sample = ctx.getImageData(
        Math.floor(canvas.width / 2) - 10,
        Math.floor(canvas.height / 2) - 10,
        20,
        20
      );

      const avg =
        sample.data.reduce((s, v, i) => (i % 4 === 3 ? s : s + v), 0) /
        (sample.data.length * 0.75);

      if (avg < 5) {
        rafRef.current = requestAnimationFrame(scanLoop);
        return;
      }

      setCameraReady((c) => c || true);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'dontInvert',
      });

      if (code?.data) {
        const val = code.data.trim().toUpperCase();
        stopScan();
        setCarrinhoInput(val);
        setTimeout(() => carrinhoInputRef.current?.focus(), 50);
        return;
      }
    }

    rafRef.current = requestAnimationFrame(scanLoop);
  }, [stopScan]);

  const startCarrinhoScan = async () => {
    setScanError('');
    setCameraReady(false);

    if (!navigator.mediaDevices?.getUserMedia) {
      setScanError('Câmara não suportada. A app precisa de HTTPS.');
      return;
    }

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
      });
    } catch {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: true });
      } catch (e2) {
        const msg =
          e2.name === 'NotAllowedError'
            ? 'Permissão de câmara negada. Verifique as definições do browser.'
            : e2.name === 'NotFoundError'
              ? 'Câmara não encontrada neste dispositivo.'
              : 'Erro ao aceder à câmara: ' + e2.message;
        setScanError(msg);
        return;
      }
    }

    streamRef.current = stream;
    setScanningCarrinho(true);

    const video = videoRef.current;
    if (!video) {
      stopScan();
      return;
    }

    // Same approach as MobilePickup.jsx
    video.style.transform = 'translateZ(0)';
    video.style.webkitTransform = 'translateZ(0)';
    video.setAttribute('playsinline', 'true');
    video.setAttribute('webkit-playsinline', 'true');
    video.setAttribute('muted', 'true');
    video.muted = true;
    video.srcObject = stream;

    await new Promise((resolve) => {
      const go = () => {
        video.removeEventListener('canplay', go);
        video.play().catch(() => {}).finally(resolve);
      };

      video.addEventListener('canplay', go);

      setTimeout(() => {
        video.removeEventListener('canplay', go);
        video.play().catch(() => {}).finally(resolve);
      }, 1500);
    });

    rafRef.current = requestAnimationFrame(scanLoop);
  };

  // ── Advance ────────────────────────────────────────────────────────────────
  const doAdvance = async (item, containerMaterial = null) => {
    setAdvancing(item.preparation_id);
    try {
      const r = await fastReactAPI.advancePreparationStatus(item.preparation_id, containerMaterial);

      if (r.data.success) {
        setItems((prev) =>
          prev.map((it) =>
            it.preparation_id === item.preparation_id
              ? {
                  ...it,
                  status_line: r.data.status_line,
                  container_material: r.data.container_material,
                }
              : it
          )
        );

        if (setup) {
          const updatedSetup = {
            ...setup,
            prepared_items: r.data.prepared_count,
            total_items: r.data.total_count,
            is_completed: r.data.is_completed,
            status: r.data.po_setup_status ?? setup.status,
            container_material: r.data.container_material ?? setup.container_material,
          };

          setSetup(updatedSetup);

          // Auto-queue print when all items complete — except when this
          // completion came from advancing a restocked line (no label
          // needed for restocks).
          if (r.data.is_completed && !r.data.was_restock) handlePrintLabel();
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setAdvancing(null);
    }
  };

  const handleAdvance = (item) => {
    if (item.status_line === 'COMPLETED' || advancing) return;
    if (item.status_line === 'READY' || item.status_line === 'COMPLETED') return;

    if (item.status_line === 'SETUP' && !setup?.container_material) {
      setCarrinhoInput('');
      setPendingItem(item);
      setScanError('');
      setShowCarrinho(true);
      setTimeout(() => carrinhoInputRef.current?.focus(), 80);
    } else {
      doAdvance(item, setup?.container_material ?? null);
    }
  };

  const handleCarrinhoConfirm = () => {
    if (!pendingItem) return;
    doAdvance(pendingItem, carrinhoInput.trim() || null);
    stopScan();
    setShowCarrinho(false);
    setPendingItem(null);
    setScanError('');
  };

  // ── Revert ─────────────────────────────────────────────────────────────────
  const handleRevert = async (item) => {
    if (item.status_line === 'SETUP' || advancing) return;

    setAdvancing(item.preparation_id);
    try {
      const r = await fastReactAPI.revertPreparationStatus(item.preparation_id);
      if (r.data.success) {
        setItems((prev) =>
          prev.map((it) =>
            it.preparation_id === item.preparation_id
              ? { ...it, status_line: r.data.status_line }
              : it
          )
        );

        if (setup) {
          setSetup((prev) => ({
            ...prev,
            prepared_items: r.data.prepared_count,
            total_items: r.data.total_count,
            is_completed: r.data.is_completed,
            status: r.data.po_setup_status ?? prev.status,
          }));
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setAdvancing(null);
    }
  };

  const handlePrintLabel = async () => {
    setPrintState('loading');
    try {
      const r = await fastReactAPI.queuePrintLabel(poSetupId);
      setPrintState('success');
      setPrintMsg(r.data.message || 'Etiqueta enviada para impressão');
    } catch (e) {
      setPrintState('error');
      setPrintMsg(e.response?.data?.error || 'Erro ao enviar etiqueta');
    } finally {
      setTimeout(() => setPrintState(null), 4000);
    }
  };

  // ── Restock ────────────────────────────────────────────────────────────────
  // One-shot action: entering the cone count directly completes the line —
  // no container material step, no separate "start" click (mirrors the
  // desktop Reabastecer flow).
  const handleRestockClick = (item) => {
    setRestockInput(item.num_restock_cones != null ? String(item.num_restock_cones) : '');
    setPendingRestockItem(item);
    setShowRestock(true);
    setTimeout(() => restockInputRef.current?.focus(), 80);
  };

  const handleRestockConfirm = async () => {
    if (!pendingRestockItem) return;
    const cones = parseInt(restockInput, 10);
    if (!Number.isFinite(cones) || cones < 0) {
      setPrintState('error');
      setPrintMsg('Indique um número de canos válido.');
      setTimeout(() => setPrintState(null), 4000);
      return;
    }

    setRestocking(true);
    try {
      const r = await fastReactAPI.markRestock(pendingRestockItem.preparation_id, cones);
      if (r.data.success) {
        const { status_line, num_restock_cones, po_setup_status, is_completed } = r.data;

        // container_material is cleared for the WHOLE po_setup on the
        // backend, not just this line — mirror that in local state too.
        setItems((prev) =>
          prev.map((it) =>
            it.preparation_id === pendingRestockItem.preparation_id
              ? { ...it, status_line, num_restock_cones, container_material: null }
              : { ...it, container_material: null }
          )
        );

        if (setup) {
          setSetup((prev) => ({
            ...prev,
            status: po_setup_status ?? prev.status,
            container_material: null,
            is_completed: is_completed ?? prev.is_completed,
          }));
        }

        setPrintState('success');
        setPrintMsg('Linha marcada para reabastecimento');
      }
    } catch (e) {
      setPrintState('error');
      setPrintMsg(e.response?.data?.error || 'Erro ao marcar reabastecimento');
    } finally {
      setTimeout(() => setPrintState(null), 4000);
      setRestocking(false);
      setShowRestock(false);
      setPendingRestockItem(null);
    }
  };

  // ── Computed ───────────────────────────────────────────────────────────────
  const pct =
    setup?.total_items > 0
      ? Math.round((setup.prepared_items / setup.total_items) * 100)
      : 0;

  const sortedItems = [
    ...items.filter((i) => i.status_line !== 'READY' && i.status_line !== 'COMPLETED'),
    ...items.filter((i) => i.status_line === 'READY'),
    ...items.filter((i) => i.status_line === 'COMPLETED'),
  ];

  const PrepItem = ({ item }) => {
    const isCompleted = item.status_line === 'COMPLETED';
    const isSpinning = advancing === item.preparation_id;

    return (
      <div className={`mb-prep-item ${isCompleted ? 'mb-prep-item--completed' : ''}`}>
        <div className="mb-prep-info">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span className="mb-prep-code">{item.product_code}</span>
            {item.variant_code && (
              <span className="mb-prep-code"> - {item.variant_code}</span>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginTop: 1 }}>
            {item.lot_no && (
              <span className="mb-prep-code">Lote: {item.lot_no}</span>
            )}
            {item.location_position && (
              <span
                style={{
                  fontSize: 11,
                  color: 'var(--gray-800)',
                  background: 'var(--gray-100)',
                  borderRadius: 4,
                  padding: '1px 5px',
                  fontFamily: 'monospace',
                }}
              >
                📍 {item.location_position}
              </span>
            )}
          </div>

          {item.product_description && (
            <div style={{ fontSize: 11, color: 'var(--gray-800)', marginTop: 1 }}>
              {item.product_description}
            </div>
          )}

          <div className="mb-prep-status-row" style={{ marginTop: 5 }}>
            {itemBadge(item.status_line)}

            {item.status_line !== 'SETUP' && (
              <button
                type="button"
                className="mb-undo-btn"
                onClick={() => handleRevert(item)}
                disabled={!!advancing}
              >
                <FiRotateCcw size={11} />
              </button>
            )}

            {item.status_line === 'COMPLETED' && (
              <button
                type="button"
                className="mb-undo-btn"
                onClick={() => handleRestockClick(item)}
                disabled={!!advancing}
                title="Marcar para reabastecimento"
              >
                <FiPackage size={11} />
              </button>
            )}

            {item.num_restock_cones != null && (
              <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                Cones {item.num_restock_cones}
              </span>
            )}

            {item.container_material && (
              <span style={{ fontSize: 13, color: 'var(--gray-900)', marginLeft: 2 }}>
                🛒 {item.container_material}
              </span>
            )}
          </div>
        </div>

        <div className="mb-prep-right">
          <span className="mb-prep-qty">{fmtQty(item.quantity)}</span>
          <button
            type="button"
            className={advanceBtnClass(item.status_line)}
            onClick={() => handleAdvance(item)}
            disabled={!!advancing || isCompleted}
          >
            {isSpinning ? (
              <div className="mb-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
            ) : (
              advanceBtnIcon(item.status_line)
            )}
          </button>
        </div>
      </div>
    );
  };

  return (
    <MobileLayout
      title={setup?.po_code_line_full || 'Preparação'}
      backTo="/mobile/plano-teares"
    >
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {loading ? (
        <div className="mb-state">
          <div className="mb-spinner" />
        </div>
      ) : (
        <>
          {setup && (
            <div className="mb-detail-header">
              <div className="mb-detail-po">{setup.po_code_line_full}</div>

              {setup.product_name && (
                <div className="mb-detail-product">
                  {setup.product_name}
                  {setup.customer_name ? ` · ${setup.customer_name}` : ''}
                </div>
              )}

              <div className="mb-detail-meta">
                {setup.machine && <span>Máquina {setup.machine}</span>}
                {setup.strip_start && (
                  <span>{new Date(setup.strip_start).toLocaleDateString('pt-PT')}</span>
                )}
                {setup.is_completed ? (
                  <span className="mb-badge mb-badge--completed">Recolhido</span>
                ) : setup.status === 'READY' ? (
                  <span className="mb-badge mb-badge--completed">Pronto p/ Recolha</span>
                ) : setup.status === 'IN_PROGRESS' ? (
                  <span className="mb-badge mb-badge--inprog">Em Preparação</span>
                ) : setup.status === 'SETUP' ? (
                  <span className="mb-badge mb-badge--setup">SETUP</span>
                ) : setup.status === 'RESTOCK' ? (
                  <span className="mb-badge mb-badge--restock">REABASTECER</span>
                ) : null}
              </div>

              {setup.container_material && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginTop: 10,
                  }}
                >
                  <div className="mb-detail-container">🛒 {setup.container_material}</div>
                  <button
                    type="button"
                    className="mb-icon-btn"
                    style={{ color: printState === 'loading' ? 'var(--gray-300)' : 'var(--primary)' }}
                    onClick={handlePrintLabel}
                    disabled={printState === 'loading'}
                    title="Imprimir etiqueta"
                  >
                    <FiPrinter size={18} />
                  </button>
                </div>
              )}

              {setup.total_items > 0 && (
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div className="mb-prog-bar" style={{ flex: 1, height: 6 }}>
                    <div className="mb-prog-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="mb-prog-label">
                    {setup.prepared_items}/{setup.total_items}
                  </span>
                </div>
              )}
            </div>
          )}

          {sortedItems.length === 0 ? (
            <div className="mb-state">
              <span>Sem itens de preparação</span>
            </div>
          ) : (
            <div className="mb-prep-list">
              {sortedItems.map((item) => (
                <PrepItem key={item.preparation_id} item={item} />
              ))}
            </div>
          )}
        </>
      )}

      {printState && (
        <div
          style={{
            position: 'fixed',
            bottom: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 300,
            background:
              printState === 'success'
                ? '#dcfce7'
                : printState === 'error'
                  ? '#fee2e2'
                  : 'white',
            border: `1px solid ${
              printState === 'success'
                ? '#86efac'
                : printState === 'error'
                  ? '#fca5a5'
                  : 'var(--gray-300)'
            }`,
            borderRadius: 10,
            padding: '10px 16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            fontSize: 12,
            fontWeight: 600,
            color: printState === 'error' ? '#dc2626' : 'var(--gray-900)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            whiteSpace: 'nowrap',
          }}
        >
          {printState === 'loading' && (
            <div className="mb-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
          )}
          {printState === 'success' && '✅'}
          {printState === 'error' && '❌'}
          {printState === 'loading' ? 'A gerar etiqueta…' : printMsg}
        </div>
      )}

      {showCarrinho && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'flex-end',
            zIndex: 200,
          }}
          onClick={() => {
            stopScan();
            setShowCarrinho(false);
            setPendingItem(null);
            setScanError('');
          }}
        >
          <div
            style={{
              background: 'white',
              width: '100%',
              maxWidth: 480,
              margin: '0 auto',
              borderRadius: '16px 16px 0 0',
              padding: '20px 20px 24px',
              boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
              maxHeight: '92dvh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 8,
              }}
            >
              <div style={{ fontSize: 16, fontWeight: 700 }}>🛒 Identificar Carrinho</div>
              <button
                type="button"
                onClick={() => {
                  stopScan();
                  setShowCarrinho(false);
                  setPendingItem(null);
                  setScanError('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 20,
                  color: 'var(--gray-400)',
                  cursor: 'pointer',
                  padding: '0 4px',
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>

            <div style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 14 }}>
              Indique o número do carrinho ou leia o QR code.
            </div>

            {/* camera always mounted, only hidden/shown */}
            <div style={{ display: scanningCarrinho ? 'block' : 'none', marginBottom: 12 }}>
              <div
                style={{
                  position: 'relative',
                  background: '#000',
                  width: '100%',
                  paddingTop: '100%',
                  borderRadius: 12,
                  overflow: 'hidden',
                }}
              >
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'fill',
                    display: 'block',
                  }}
                />

                {scanningCarrinho && !cameraReady && (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(0,0,0,0.6)',
                    }}
                  >
                    <span style={{ color: 'white', fontSize: 13 }}>A iniciar câmara…</span>
                  </div>
                )}

                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <div style={{ width: '58%', aspectRatio: '1', position: 'relative' }}>
                    {[
                      ['top', 'left'],
                      ['top', 'right'],
                      ['bottom', 'left'],
                      ['bottom', 'right'],
                    ].map(([v, h]) => (
                      <div
                        key={v + h}
                        style={{
                          position: 'absolute',
                          [v]: 0,
                          [h]: 0,
                          width: 20,
                          height: 20,
                          borderTop: v === 'top' ? '3px solid var(--primary)' : 'none',
                          borderBottom: v === 'bottom' ? '3px solid var(--primary)' : 'none',
                          borderLeft: h === 'left' ? '3px solid var(--primary)' : 'none',
                          borderRight: h === 'right' ? '3px solid var(--primary)' : 'none',
                        }}
                      />
                    ))}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={stopScan}
                  style={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    background: 'rgba(0,0,0,0.65)',
                    border: 'none',
                    color: 'white',
                    borderRadius: 8,
                    padding: '6px 12px',
                    cursor: 'pointer',
                    fontSize: 13,
                  }}
                >
                  <FiX size={14} /> Cancelar
                </button>
              </div>
            </div>

            {!scanningCarrinho && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <input
                  ref={carrinhoInputRef}
                  type="text"
                  style={{
                    flex: 1,
                    height: 48,
                    border: '1px solid var(--gray-300)',
                    borderRadius: 10,
                    padding: '0 14px',
                    fontSize: 16,
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                  placeholder="Ex: C-001"
                  value={carrinhoInput}
                  onChange={(e) => setCarrinhoInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCarrinhoConfirm();
                  }}
                  autoFocus
                />

                <button
                  type="button"
                  onClick={startCarrinhoScan}
                  style={{
                    width: 50,
                    height: 50,
                    border: '1px solid var(--gray-300)',
                    borderRadius: 10,
                    background: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--primary)',
                    flexShrink: 0,
                  }}
                  title="Ler com câmara"
                >
                  <FiCamera size={20} />
                </button>
              </div>
            )}

            {scanError && (
              <div
                style={{
                  background: '#fee2e2',
                  color: 'var(--error)',
                  border: '1px solid #fca5a5',
                  borderRadius: 8,
                  padding: '10px 12px',
                  fontSize: 12,
                  marginBottom: 12,
                  display: 'flex',
                  gap: 8,
                  alignItems: 'flex-start',
                }}
              >
                <FiAlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>{scanError}</span>
              </div>
            )}

            <button
              type="button"
              className="mb-btn-primary"
              style={{ marginTop: 0 }}
              onClick={handleCarrinhoConfirm}
            >
              Confirmar
            </button>
          </div>
        </div>
      )}

      {showRestock && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'flex-end',
            zIndex: 200,
          }}
          onClick={() => {
            setShowRestock(false);
            setPendingRestockItem(null);
          }}
        >
          <div
            style={{
              background: 'white',
              width: '100%',
              maxWidth: 480,
              margin: '0 auto',
              borderRadius: '16px 16px 0 0',
              padding: '20px 20px 24px',
              boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
              maxHeight: '92dvh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 8,
              }}
            >
              <div style={{ fontSize: 16, fontWeight: 700 }}>📦 Reabastecer</div>
              <button
                type="button"
                onClick={() => {
                  setShowRestock(false);
                  setPendingRestockItem(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 20,
                  color: 'var(--gray-400)',
                  cursor: 'pointer',
                  padding: '0 4px',
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>

            <div style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 14 }}>
              Indique o número de canos reabastecidos para esta linha.
            </div>

            <input
              ref={restockInputRef}
              type="number"
              min="0"
              step="1"
              style={{
                width: '100%',
                height: 48,
                border: '1px solid var(--gray-300)',
                borderRadius: 10,
                padding: '0 14px',
                fontSize: 16,
                outline: 'none',
                boxSizing: 'border-box',
                marginBottom: 14,
              }}
              placeholder="Ex: 12"
              value={restockInput}
              onChange={(e) => setRestockInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRestockConfirm();
              }}
              autoFocus
            />

            <button
              type="button"
              className="mb-btn-primary"
              style={{ marginTop: 0 }}
              onClick={handleRestockConfirm}
              disabled={restocking}
            >
              {restocking ? 'A processar…' : 'Confirmar'}
            </button>
          </div>
        </div>
      )}
    </MobileLayout>
  );
};

export default MobilePrepDetail;